/**
 * advance-structures.ts — per-tick planet-side structure pass.
 *
 * Runs inside advanceTick after advancePopulationAndEconomy (so upkeep
 * deducts against income that landed this tick) and before the ship
 * advance passes. Per active structure it does three things:
 *
 *   1. Deduct upkeep (STRUCTURE_UPKEEP_CREDITS_PER_TICK credits) from the
 *      owning empire. Only active structures (level >= 1) pay — a structure
 *      under initial construction is not yet operational. Empires are
 *      allowed to go negative on credits (matches how jumps consume fuel
 *      without requiring positive balance upfront); a sustained negative
 *      balance becomes a player concern, not a sim-layer error.
 *
 *   2. Refresh maxHull from the empire's current Hull tech multiplier
 *      (`effectiveStructureMaxHull`), then auto-repair currentHull
 *      toward maxHull at STRUCTURE_AUTO_REPAIR_PROPORTION_PER_TICK ×
 *      maxHull per tick. Hull tech researched mid-game grows every
 *      existing structure's ceiling on the next tick (parity with the
 *      ship combat dispatch which reads `effectiveMaxHull` live).
 *
 *   3. Tick the build/upgrade countdown. On reaching 0, promote level++
 *      and refit hull to the new level's ceiling. Structures that promote
 *      this tick start paying upkeep next tick (upkeep ran before the
 *      promotion check intentionally — you're billed for what you had at
 *      the start of the tick, not what you're getting at the end).
 *
 * Deterministic iteration: structures visited in ascending entity id so
 * the tick is reproducible across hosts (MULTIPLAYER §2).
 */

import {
  autoRepairProportion,
  upkeepForKind,
} from './balance-structures';
import { effectiveStructureMaxHull } from './research-effects';
import type { Empire, GameState, Structure } from '../state/types/index';

/**
 * Advance planet-side structures by exactly one tick. Mutates state.
 *
 * Construction is **sequential per planet (FIFO by structure id)**: if a
 * planet has multiple in-flight structures (either pending first-builds
 * or queued upgrades), only the lowest-id one ticks down each tick;
 * later-queued structures wait their turn. Upkeep and auto-repair still
 * apply in parallel to every active structure — sequentiality only gates
 * the construction pipeline, not maintenance.
 *
 * Why sequential: matches the shipyard build queue pattern (one active
 * build, others wait) and avoids the earlier behaviour where queuing N
 * structures silently started N parallel builds. A planet has one
 * construction capacity; the order is the order the player clicked.
 */
export function advanceStructures(state: GameState): void {
  const planets = [...state.planets.values()].sort((a, b) => a.id - b.id);
  const sd = state.structuresData;
  const repairFraction = autoRepairProportion(sd);

  for (const planet of planets) {
    if (planet.ownerEmpireId === null) continue;
    const empire = state.empires.get(planet.ownerEmpireId);
    if (!empire) continue;

    // Gather this planet's structures.
    const planetStructures: Structure[] = [];
    for (const sid of planet.structureIds) {
      const s = state.structures.get(sid);
      if (s) planetStructures.push(s);
    }

    // 1. Upkeep + auto-repair for every ACTIVE structure on the planet.
    //    These two run in parallel — every commissioned structure bills
    //    its upkeep each tick and self-heals its hull independently.
    //    Order doesn't matter here, but sort by id so the pass stays
    //    deterministic (e.g. empire.resources.credits is mutated once
    //    per active structure; order is observable via save diffing).
    planetStructures.sort((a, b) => a.id - b.id);
    for (const structure of planetStructures) {
      if (structure.level < 1) continue;
      empire.resources.credits -= upkeepForKind(sd, structure.kind);
      // Refresh maxHull live from the empire's current Hull tech
      // multiplier — matches the ship pattern where Hull tech grows
      // existing units' ceiling immediately (effectiveMaxHull is read
      // per-frame in combat dispatch). Defensive fallback if the xlsx
      // lookup returns 0 (kind/level missing): keep the stored value.
      const live = effectiveStructureMaxHull(state, empire, structure.kind, structure.level);
      if (live > 0) structure.maxHull = live;
      if (structure.currentHull < structure.maxHull) {
        const repair = structure.maxHull * repairFraction;
        structure.currentHull = Math.min(
          structure.maxHull,
          structure.currentHull + repair,
        );
      }
    }

    // 2. Construction / upgrade — tick the structure the player queued
    //    FIRST among all in-flight ones on this planet (lowest queuedAt
    //    timestamp). Id is the deterministic tie-breaker when two
    //    commands land on the same tick. This preserves player click
    //    order rather than underlying build order, so upgrading an
    //    older structure after a newer one doesn't let the older jump
    //    the queue.
    let active: Structure | null = null;
    for (const structure of planetStructures) {
      if (structure.buildTicksRemaining <= 0) continue;
      if (
        active === null ||
        structure.queuedAt < active.queuedAt ||
        (structure.queuedAt === active.queuedAt && structure.id < active.id)
      ) {
        active = structure;
      }
    }
    if (active) {
      active.buildTicksRemaining -= 1;
      if (active.buildTicksRemaining === 0) {
        promoteLevel(state, empire, active);
      }
    }
  }
}

/**
 * Advance a structure's active level by one and refit hull to the new
 * ceiling. Called the tick a buildTicksRemaining countdown hits 0 —
 * either finishing an initial construction (level 0 → 1) or finishing
 * an upgrade (N → N+1).
 *
 * Refits currentHull = maxHull on promotion (upgrade "includes a refit"
 * semantics) so a damaged structure comes out of an upgrade fully healed.
 * Level is clamped to 6 defensively; the command layer refuses to queue
 * an upgrade past 6 but keep the clamp here so a bad state can't explode.
 *
 * New ceiling consults `effectiveStructureMaxHull` so the empire's
 * current Hull tech multiplier applies at promote time (matches the
 * ship pattern where freshly-built ships pick up Hull tech via
 * `effectiveMaxHull`).
 */
function promoteLevel(state: GameState, empire: Empire, structure: Structure): void {
  const nextLevel = Math.min(6, structure.level + 1);
  const lookup = effectiveStructureMaxHull(state, empire, structure.kind, nextLevel);
  const newMaxHull = lookup > 0 ? lookup : structure.maxHull;
  structure.level = nextLevel;
  structure.maxHull = newMaxHull;
  structure.currentHull = newMaxHull;
}
